import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { api } from './services/api';
import FoodInput from './components/FoodInput';
import NutritionChecklist from './components/NutritionChecklist';
import LanguageSwitcher from './components/LanguageSwitcher';
import FoodSafetyResult from './components/FoodSafetyResult';
import FoodHistory from './components/FoodHistory';
import ProgressTracker from './components/ProgressTracker';
import FoodSearch from './components/FoodSearch';
import PopularFoods from './components/PopularFoods';
import FoodManagement from './components/FoodManagement';
import FoodHistoryManagement from './components/FoodHistoryManagement';
import PregnancyTracker from './components/PregnancyTracker';
import PostpartumView from './components/PostpartumView';
import PostpartumOverview from './components/postpartum/PostpartumOverview';
import PostpartumRecovery from './components/postpartum/PostpartumRecovery';
import PostpartumNutrition from './components/postpartum/PostpartumNutrition';
import PostpartumAppointments from './components/postpartum/PostpartumAppointments';
import PostpartumWarnings from './components/postpartum/PostpartumWarnings';
import DailyTips from './components/DailyTips';
import KickCounter from './components/KickCounter';
import ContractionTimer from './components/ContractionTimer';
import SymptomChecker from './components/SymptomChecker';
import MedicationChecker from './components/MedicationChecker';
import AppointmentReminder from './components/AppointmentReminder';
import OAuth2Success from './components/auth/OAuth2Success';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedAdminRoute from './components/admin/ProtectedAdminRoute';
import { seoLandingPages } from './components/landing/seoLandingPages';
import './services/i18n';
import './App.css';

// Lazy-load heavy route components so initial bundle is smaller and first paint is faster
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const VerifyEmail = lazy(() => import('./components/auth/VerifyEmail'));
const VerifyPhone = lazy(() => import('./components/auth/VerifyPhone'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const ChangePassword = lazy(() => import('./components/auth/ChangePassword'));
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow'));
const PrivacyPolicy = lazy(() => import('./components/privacy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/privacy/TermsOfService'));
const UserProfile = lazy(() => import('./components/profile/UserProfile'));
const SubscriptionManagement = lazy(() => import('./components/billing/SubscriptionManagement'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const NotificationCenter = lazy(() => import('./components/notifications/NotificationCenter'));
const NotificationPreferences = lazy(() => import('./components/notifications/NotificationPreferences'));
const BlogPage = lazy(() => import('./components/landing/BlogPage'));
const BlogPost = lazy(() => import('./components/landing/BlogPost'));
const SeoLandingPage = lazy(() => import('./components/landing/SeoLandingPage'));
const AboutUs = lazy(() => import('./components/info/AboutUs'));
const Contact = lazy(() => import('./components/info/Contact'));

const RouteFallback = () => <LoadingSpinner message="Loading…" />;

function App() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const [selectedCulture, setSelectedCulture] = useState('ETHIOPIAN');
  const [foodResult, setFoodResult] = useState(null);
  const [activeTab, setActiveTab] = useState('pregnancy'); // Tab state for landing page
  // Use authenticated user ID or fallback to localStorage
  const [userId, setUserId] = useState(() => {
    let id = localStorage.getItem('pregAppUserId');
    if (!id) {
      id = `user-${Date.now()}`;
      localStorage.setItem('pregAppUserId', id);
    }
    return id;
  });
  
  // Update userId when user logs in or out
  useEffect(() => {
    const authenticatedId = user?.userId || user?.id;
    if (authenticatedId) {
      const resolvedId = String(authenticatedId);
      localStorage.setItem('pregAppUserId', resolvedId);
      setUserId(resolvedId);
      return;
    }

    if (!isAuthenticated) {
      let id = localStorage.getItem('pregAppUserId');
      if (!id) {
        id = `user-${Date.now()}`;
        localStorage.setItem('pregAppUserId', id);
      }
      setUserId(id);
    }
  }, [user, isAuthenticated]);

  return (
    <Router>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Landing Page - Show to non-authenticated users */}
        <Route path="/" element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : <LandingPage />
        } />
        
        {/* Authentication Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/oauth2/success" element={<OAuth2Success />} />
        
        {/* Onboarding Route - Protected, but accessible if not completed */}
        <Route 
          path="/onboarding" 
          element={
            <ProtectedRoute>
              <OnboardingFlow />
            </ProtectedRoute>
          } 
        />
        
        {/* Dashboard Routes - Protected, requires onboarding completion */}
        <Route 
          path="/dashboard/*" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-phone" element={<VerifyPhone />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Profile Routes */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/change-password" 
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          } 
        />
        
        {/* Billing Route */}
        <Route 
          path="/subscription" 
          element={
            <ProtectedRoute>
              <SubscriptionManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <ProtectedAdminRoute>
              <AdminDashboard />
            </ProtectedAdminRoute>
          } 
        />
        
        {/* Notification Center */}
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <NotificationCenter />
            </ProtectedRoute>
          } 
        />
        
        {/* Notification Preferences */}
        <Route 
          path="/notifications/preferences" 
          element={
            <ProtectedRoute>
              <NotificationPreferences />
            </ProtectedRoute>
          } 
        />
        
        {/* Dashboard Profile Sub-routes are handled by Dashboard's nested routing */}
        
        {/* Privacy (public for store & registration links) & Terms */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route 
          path="/terms" 
          element={
            <ProtectedRoute>
              <TermsOfService />
            </ProtectedRoute>
          } 
        />
        
        {/* Info Pages */}
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/pregnancy-week-by-week" element={<SeoLandingPage page={seoLandingPages.weekByWeek} />} />
        <Route path="/pregnancy-nutrition-by-week" element={<SeoLandingPage page={seoLandingPages.nutritionByWeek} />} />
        <Route path="/pregnancy-appointments-guide" element={<SeoLandingPage page={seoLandingPages.appointmentsGuide} />} />
        
        {/* Legacy Home Route - Redirect to Dashboard */}
        <Route path="/home" element={
          <div className="App">
            <header className="App-header">
              <h1>🤱 {t('app.name')}</h1>
              <div className="header-actions">
                <LanguageSwitcher />
                {isAuthenticated ? (
                  <div className="user-menu">
                    <span className="user-name">{user?.firstName || user?.email}</span>
                    <div className="user-menu-dropdown">
                      <Link to="/profile">{t('app.legacy.myProfile')}</Link>
                      <Link to="/notifications">
                        🔔 {t('app.legacy.notifications')}
                        {user?.unreadNotifications > 0 && (
                          <span className="notification-badge">{user.unreadNotifications}</span>
                        )}
                      </Link>
                      <Link to="/subscription">{t('app.legacy.subscription')}</Link>
                      <Link to="/privacy">{t('app.legacy.privacyConsent')}</Link>
                      {(user?.roles?.some(role => role === 'ROLE_ADMIN' || role === 'ADMIN') || user?.role === 'ADMIN' || user?.role === 'ROLE_ADMIN') && (
                        <Link to="/admin">{t('app.legacy.adminPanel')}</Link>
                      )}
                      <button onClick={logout}>{t('app.legacy.logout')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="auth-buttons">
                    <Link to="/login" className="auth-link">{t('app.legacy.login')}</Link>
                    <Link to="/register" className="auth-link register">{t('app.legacy.signUp')}</Link>
                  </div>
                )}
              </div>
            </header>

            <main className="App-main">
              <section className="culture-selector">
                <label>
                  🌍 {t('culture.select')}:
                  <select 
                    value={selectedCulture} 
                    onChange={(e) => setSelectedCulture(e.target.value)}
                  >
                    <option value="ETHIOPIAN">🇪🇹 {t('culture.ethiopian')}</option>
                    <option value="ERITREAN">🇪🇷 {t('culture.eritrean')}</option>
                    <option value="SPANISH">🇪🇸 {t('culture.spanish')}</option>
                  </select>
                </label>
              </section>

              <div className="main-content">
                {/* Pregnancy Tracking Section */}
                <section className="content-section pregnancy-section">
                  <PregnancyTracker userId={userId} />
                </section>

                {/* Tabs Navigation */}
                <div className="tabs-container">
                  <div className="tabs-nav">
                    <button 
                      className={`tab-button ${activeTab === 'pregnancy' ? 'active' : ''}`}
                      onClick={() => setActiveTab('pregnancy')}
                    >
                      🤱 {t('app.legacy.tabPregnancy')}
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'food' ? 'active' : ''}`}
                      onClick={() => setActiveTab('food')}
                    >
                      🍽️ {t('app.legacy.tabFoodNutrition')}
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
                      onClick={() => setActiveTab('progress')}
                    >
                      📊 {t('app.legacy.tabProgressHistory')}
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
                      onClick={() => setActiveTab('management')}
                    >
                      ⚙️ {t('app.legacy.tabManagement')}
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'tools' ? 'active' : ''}`}
                      onClick={() => setActiveTab('tools')}
                    >
                      🛠️ {t('app.legacy.tabToolsResources')}
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="tabs-content">
                  {/* Pregnancy Tab */}
                  {activeTab === 'pregnancy' && (
                    <div className="tab-panel">
                      <DailyTips userId={userId} />
                      <KickCounter userId={userId} />
                      <ContractionTimer userId={userId} />
                      <SymptomChecker userId={userId} />
                      <AppointmentReminder userId={userId} />
                    </div>
                  )}

                  {/* Food & Nutrition Tab */}
                  {activeTab === 'food' && (
                    <div className="tab-panel">
                      <MedicationChecker />
                      <section className="quick-actions-section">
                        <PopularFoods 
                          culture={selectedCulture} 
                          userId={userId}
                          onSelect={setFoodResult} 
                        />
                        <FoodSearch 
                          culture={selectedCulture} 
                          userId={userId}
                          onSelect={setFoodResult} 
                        />
                        <FoodInput 
                          culture={selectedCulture}
                          userId={userId}
                          onResult={setFoodResult}
                        />
                        {foodResult && <FoodSafetyResult result={foodResult} />}
                      </section>
                    </div>
                  )}

                  {/* Progress & History Tab */}
                  {activeTab === 'progress' && (
                    <div className="tab-panel">
                      <div className="main-content-grid">
                        <div className="left-column">
                          <section className="content-section">
                            <h2 className="section-title">📊 Your Progress</h2>
                            <ProgressTracker userId={userId} />
                            <NutritionChecklist userId={userId} />
                          </section>
                        </div>
                        
                        <div className="right-column">
                          <section className="content-section">
                            <h2 className="section-title">📜 Your History</h2>
                            <FoodHistory userId={userId} />
                          </section>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Management Tab */}
                  {activeTab === 'management' && (
                    <div className="tab-panel">
                      <div className="main-content-grid">
                        <div className="left-column">
                          <section className="content-section">
                            <h2 className="section-title">🍽️ Food Database</h2>
                            <FoodManagement />
                          </section>
                        </div>
                        
                        <div className="right-column">
                          <section className="content-section">
                            <h2 className="section-title">📋 History Management</h2>
                            <FoodHistoryManagement userId={userId} />
                          </section>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tools & Resources Tab */}
                </div>
              </div>
            </main>
          </div>
        } />

        {/* Postpartum Routes */}
        <Route 
          path="/postpartum/:userId" 
          element={
            <ProtectedRoute>
              <PostpartumViewWrapper userId={userId} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/postpartum/:userId/overview" 
          element={
            <ProtectedRoute>
              <PostpartumOverview />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/postpartum/:userId/recovery" 
          element={
            <ProtectedRoute>
              <PostpartumRecovery />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/postpartum/:userId/nutrition" 
          element={
            <ProtectedRoute>
              <PostpartumNutrition />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/postpartum/:userId/appointments" 
          element={
            <ProtectedRoute>
              <PostpartumAppointments />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/postpartum/:userId/warnings" 
          element={
            <ProtectedRoute>
              <PostpartumWarnings />
            </ProtectedRoute>
          } 
        />
      </Routes>
      </Suspense>
    </Router>
  );
}

// Wrapper component to get userId from params and pass to PostpartumView
function PostpartumViewWrapper() {
  const { userId } = useParams();
  const [profile, setProfile] = React.useState(null);
  
  React.useEffect(() => {
    const loadProfile = async () => {
      try {
        const profileData = await api.getPregnancyProfile(userId).catch(() => null);
        setProfile(profileData);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [userId]);

  const handleProfileUpdate = async () => {
    try {
      const profileData = await api.getPregnancyProfile(userId).catch(() => null);
      setProfile(profileData);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  return <PostpartumView userId={userId} profile={profile} onProfileUpdate={handleProfileUpdate} />;
}

export default App;

